import {useState} from 'react'
import {Text as RNText, View} from 'react-native'
import Animated from 'react-native-reanimated'
import {msg, plural} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {useNavigationState} from '@react-navigation/native'

import {useMinimalShellFooterTransform} from '#/lib/hooks/useMinimalShellTransform'
import {getCurrentRoute, isTab} from '#/lib/routes/helpers'
import {makeProfileLink} from '#/lib/routes/links'
import {type CommonNavigatorParams} from '#/lib/routes/types'
import {useGate} from '#/lib/statsig/statsig'
import {useHomeBadge} from '#/state/home-badge'
import {useUnreadMessageCount} from '#/state/queries/messages/list-conversations'
import {useUnreadNotifications} from '#/state/queries/notifications/unread'
import {useSession} from '#/state/session'
import {Link} from '#/view/com/util/Link'
import {atoms as a, useTheme, web} from '#/alf'
import {AppLanguageDropdown} from '#/components/AppLanguageDropdown'
import {
  Bell_Filled_Corner0_Rounded as BellFilled,
  Bell_Stroke2_Corner0_Rounded as Bell,
} from '#/components/icons/Bell'
import {
  HomeOpen_Filled_Corner0_Rounded as HomeFilled,
  HomeOpen_Stoke2_Corner0_Rounded as Home,
} from '#/components/icons/HomeOpen'
import {MagnifyingGlass_Filled_Stroke2_Corner0_Rounded as MagnifyingGlassFilled} from '#/components/icons/MagnifyingGlass'
import {MagnifyingGlass2_Stroke2_Corner0_Rounded as MagnifyingGlass} from '#/components/icons/MagnifyingGlass2'
import {
  Message_Stroke2_Corner0_Rounded as Message,
  Message_Stroke2_Corner0_Rounded_Filled as MessageFilled,
} from '#/components/icons/Message'
import {
  UserCircle_Filled_Corner0_Rounded as UserCircleFilled,
  UserCircle_Stroke2_Corner0_Rounded as UserCircle,
} from '#/components/icons/UserCircle'
import {VideoCamera_Stroke2_Corner0_Rounded as _VideoCamera} from '#/components/icons/VideoCamera'
import {InlineLinkText} from '#/components/Link'
import {Text} from '#/components/Typography'
import {styles} from './BottomBarStyles'

export function BottomBarWeb() {
  const {currentAccount} = useSession()
  const t = useTheme()

  const footerMinimalShellTransform = useMinimalShellFooterTransform()
  const iconWidth = 26

  const unreadMessageCount = useUnreadMessageCount()
  const notificationCountStr = useUnreadNotifications()
  const hasHomeBadge = useHomeBadge()
  const gate = useGate()

  return (
    <Animated.View
      role="navigation"
      style={[
        styles.bottomBar,
        styles.bottomBarWeb,
        t.atoms.bg,
        t.atoms.border_contrast_low,
        footerMinimalShellTransform,
      ]}>
      <NavItem
        routeName="Home"
        href="/"
        hasNew={hasHomeBadge && gate('remove_show_latest_button')}>
        {({isActive}) => {
          const Icon = isActive ? HomeFilled : Home
          return (
            <Icon
              aria-hidden={true}
              width={iconWidth + 1}
              style={[styles.ctrlIcon, t.atoms.text, styles.homeIcon]}
            />
          )
        }}
      </NavItem>
      <NavItem routeName="Search" href="/search">
        {({isActive}) => {
          const Icon = isActive ? MagnifyingGlassFilled : MagnifyingGlass
          return (
            <Icon
              aria-hidden={true}
              width={iconWidth + 2}
              style={[styles.ctrlIcon, t.atoms.text, styles.searchIcon]}
            />
          )
        }}
      </NavItem>
      {/* Rooms tab temporarily disabled
      <NavItem routeName="Rooms" href="/rooms">
        {() => {
          return (
            <VideoCamera
              aria-hidden={true}
              width={iconWidth}
              style={styles.ctrlIcon}
              stroke={(t.atoms.text as {color: string}).color}
            />
          )
        }}
      </NavItem>
      */}
      <NavItem
        routeName="Messages"
        href="/messages"
        notificationCount={unreadMessageCount.numUnread}
        hasNew={unreadMessageCount.hasNew}>
        {({isActive}) => {
          const Icon = isActive ? MessageFilled : Message
          return (
            <Icon
              aria-hidden={true}
              width={iconWidth - 1}
              style={[styles.ctrlIcon, t.atoms.text, styles.messagesIcon]}
            />
          )
        }}
      </NavItem>
      <NavItem
        routeName="Notifications"
        href="/notifications"
        notificationCount={notificationCountStr}>
        {({isActive}) => {
          const Icon = isActive ? BellFilled : Bell
          return (
            <Icon
              aria-hidden={true}
              width={iconWidth}
              style={[styles.ctrlIcon, t.atoms.text, styles.bellIcon]}
            />
          )
        }}
      </NavItem>
      <NavItem
        routeName="Profile"
        href={
          currentAccount
            ? makeProfileLink({
                did: currentAccount.did,
                handle: currentAccount.handle,
              })
            : '/'
        }>
        {({isActive}) => {
          const Icon = isActive ? UserCircleFilled : UserCircle
          return (
            <Icon
              aria-hidden={true}
              width={iconWidth}
              style={[styles.ctrlIcon, t.atoms.text, styles.profileIcon]}
            />
          )
        }}
      </NavItem>
    </Animated.View>
  )
}

const NavItem: React.FC<{
  children: (props: {isActive: boolean}) => React.ReactChild
  href: string
  routeName: string
  hasNew?: boolean
  notificationCount?: string
}> = ({children, href, routeName, hasNew, notificationCount}) => {
  const {_} = useLingui()
  const {currentAccount} = useSession()
  const currentRoute = useNavigationState(state => {
    if (!state) {
      return {name: 'Home'}
    }
    return getCurrentRoute(state)
  })

  // Checks whether we're on someone else's profile
  const isOnDifferentProfile =
    currentRoute.name === 'Profile' &&
    routeName === 'Profile' &&
    (currentRoute.params as CommonNavigatorParams['Profile']).name !==
      currentAccount?.handle

  const isActive =
    currentRoute.name === 'Profile'
      ? isTab(currentRoute.name, routeName) &&
        (currentRoute.params as CommonNavigatorParams['Profile']).name ===
          (routeName === 'Profile'
            ? currentAccount?.handle
            : (currentRoute.params as CommonNavigatorParams['Profile']).name)
      : isTab(currentRoute.name, routeName)

  return (
    <Link
      href={href}
      style={[styles.ctrl, a.pb_lg]}
      navigationAction={isOnDifferentProfile ? 'push' : 'navigate'}
      aria-role="link"
      aria-label={routeName}
      accessible={true}>
      {children({isActive})}
      {notificationCount ? (
        <View
          style={[styles.notificationCount, styles.notificationCountWeb]}
          aria-label={_(
            msg`${plural(notificationCount, {
              one: '# unread item',
              other: '# unread items',
            })}`,
          )}>
          <Text style={styles.notificationCountLabel}>{notificationCount}</Text>
        </View>
      ) : hasNew ? (
        <View style={styles.hasNewBadge} />
      ) : null}
    </Link>
  )
}

export function LoggedOutFooter() {
  const {_} = useLingui()
  const t = useTheme()
  const [legalOpen, setLegalOpen] = useState(false)

  return (
    <View
      style={[
        web({
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 100,
        }),
      ]}>
      {legalOpen && (
        <View
          style={[
            a.px_xl,
            a.py_md,
            a.gap_sm,
            t.atoms.bg,
            t.atoms.border_contrast_low,
            web({borderTopWidth: 1}),
          ]}>
          <InlineLinkText
            to="/support/community-guidelines"
            label={_(msg`Community Guidelines`)}>
            {_(msg`Community Guidelines`)}
          </InlineLinkText>
          <InlineLinkText to="/support/tos" label={_(msg`Terms of Service`)}>
            {_(msg`Terms of Service`)}
          </InlineLinkText>
          <InlineLinkText
            to="/support/privacy-policy"
            label={_(msg`Privacy Policy`)}>
            {_(msg`Privacy Policy`)}
          </InlineLinkText>
          <InlineLinkText
            to="/support/copyright"
            label={_(msg`Copyright Policy`)}>
            {_(msg`Copyright Policy`)}
          </InlineLinkText>
          <InlineLinkText to="/support/2257" label={_(msg`2257 Disclosure`)}>
            {_(msg`2257 Disclosure`)}
          </InlineLinkText>
        </View>
      )}
      <View
        style={[
          a.w_full,
          a.flex_row,
          a.align_center,
          a.px_xl,
          a.py_lg,
          a.gap_xl,
          t.atoms.bg,
          t.atoms.border_contrast_low,
          web({borderTopWidth: 1}),
        ]}>
        <InlineLinkText to="/privacy" label={_(msg`Privacy`)}>
          {_(msg`Privacy`)}
        </InlineLinkText>
        <InlineLinkText label={_(msg`Help`)} to="/support">
          {_(msg`Help`)}
        </InlineLinkText>
        <RNText
          onPress={() => setLegalOpen(o => !o)}
          style={[{color: '#fd7c21'}, web({cursor: 'pointer'})]}
          role="button">
          {_(msg`Legal`)}
        </RNText>
        <InlineLinkText label={_(msg`About`)} to="/support/about">
          {_(msg`About`)}
        </InlineLinkText>
        <View style={a.flex_1} />
        <AppLanguageDropdown />
      </View>
    </View>
  )
}
